import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  GemsCounter,
  HeartsBar,
  LevelBadge,
  ProgressBar,
  StreakFlame,
  ThemeToggle,
  XPBadge,
} from '@fluent/ui';
import './Cluster2Review.css';

/**
 * Phase 1 Cluster 2 — Gamification primitives review page.
 * 6 primitives: XPBadge, StreakFlame, GemsCounter, HeartsBar, ProgressBar, CircularProgress.
 */
export function Cluster2Review() {
  const [xp, setXp] = useState(340);
  const [streakDays, setStreakDays] = useState(7);
  const [streakActive, setStreakActive] = useState(true);
  const [hearts, setHearts] = useState(3);
  const [lessonProgress, setLessonProgress] = useState(0.4);
  const [autoTick, setAutoTick] = useState(false);

  useEffect(() => {
    if (!autoTick) return;
    const id = window.setInterval(() => {
      setXp((v) => v + 10);
      setLessonProgress((v) => Math.min(1, Math.round((v + 0.1) * 10) / 10));
    }, 900);
    return () => window.clearInterval(id);
  }, [autoTick]);

  return (
    <main className="c2">
      <header className="c2__hero">
        <p className="c2__kicker">Phase 1 · Cluster 2</p>
        <h1>Gamification</h1>
        <p className="c2__lede">XP, streaks, progress. The carrots that bring people back.</p>
        <div className="c2__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* Home top-bar mock */}
      <section className="c2__section">
        <h2>Home top-bar composition</h2>
        <p className="c2__note">How XP + streak sit together on the sticky top bar.</p>
        <div className="c2__topbar">
          <LevelBadge level={Math.floor(xp / 500) + 1} />
          <XPBadge xp={xp} />
          <StreakFlame days={streakDays} active={streakActive} />
        </div>
        <div className="c2__controls">
          <Button variant="primary" size="sm" onClick={() => setXp((v) => v + 10)}>
            +10 XP
          </Button>
          <Button variant="neutral" size="sm" onClick={() => setStreakDays((d) => d + 1)}>
            +1 დღე
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setStreakActive((a) => !a)}>
            სერიის გადართვა
          </Button>
        </div>
      </section>

      {/* XP Badge */}
      <section className="c2__section">
        <h2>XPBadge</h2>
        <p className="c2__note">Three sizes. Number animates when XP changes (bump pulse).</p>
        <div className="c2__row">
          <XPBadge xp={42} size="sm" />
          <XPBadge xp={1250} size="md" />
          <XPBadge xp={12340} size="lg" />
        </div>
      </section>

      {/* Level Badge */}
      <section className="c2__section">
        <h2>LevelBadge</h2>
        <p className="c2__note">Sky→violet pill, gold star shield. Icon-only on the top bar; worded (&ldquo;დონე 3&rdquo;) on Profile.</p>
        <div className="c2__row">
          <LevelBadge level={3} size="sm" />
          <LevelBadge level={3} size="md" label="დონე" />
          <LevelBadge level={12} size="lg" label="დონე" />
        </div>
      </section>

      {/* Streak */}
      <section className="c2__section">
        <h2>StreakFlame</h2>
        <p className="c2__note">Active (orange) vs. inactive (gray) when today&apos;s target isn&apos;t met.</p>
        <div className="c2__row">
          <StreakFlame days={7} size="sm" />
          <StreakFlame days={7} size="md" />
          <StreakFlame days={7} size="lg" />
          <StreakFlame days={0} active={false} size="md" />
        </div>
      </section>

      {/* ProgressBar */}
      <section className="c2__section">
        <h2>ProgressBar</h2>
        <p className="c2__note">Sits above exercises, at the top of the lesson player.</p>
        <div className="c2__stack">
          <div className="c2__progress-wrap">
            <ProgressBar value={lessonProgress} label="გაკვეთილის პროგრესი" />
            <span className="c2__progress-count">{Math.round(lessonProgress * 100)}%</span>
          </div>
          <ProgressBar value={lessonProgress} tone="xp" label="XP to next level" size="lg" />
          <ProgressBar value={lessonProgress} tone="accent" label="weekly goal" size="sm" />
          <ProgressBar value={lessonProgress} tone="danger" label="time left" size="md" />
        </div>
        <div className="c2__controls">
          <Button variant="primary" size="sm" onClick={() => setLessonProgress((v) => Math.min(1, v + 0.1))}>
            +10%
          </Button>
          <Button variant="neutral" size="sm" onClick={() => setLessonProgress(0)}>
            Reset
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAutoTick((a) => !a)}>
            {autoTick ? 'Stop auto-tick' : 'Auto-tick'}
          </Button>
        </div>
      </section>

      {/* Circular */}
      <section className="c2__section">
        <h2>CircularProgress</h2>
        <p className="c2__note">Compact. Good for &ldquo;exercise 3 of 8&rdquo; HUD or XP-to-next-level dashboards.</p>
        <div className="c2__row c2__row--centered">
          <CircularProgress
            value={lessonProgress}
            size={56}
            label={`${Math.round(lessonProgress * 8)}/8`}
          />
          <CircularProgress value={lessonProgress} size={80} label={`${Math.round(lessonProgress * 100)}%`} />
          <CircularProgress
            value={lessonProgress}
            tone="xp"
            size={96}
            label={`${Math.round(lessonProgress * 100)}%`}
          />
          <CircularProgress value={lessonProgress} tone="accent" size={64} />
        </div>
      </section>

      {/* HeartsBar (unused in MVP) */}
      <section className="c2__section">
        <h2>HeartsBar · GemsCounter</h2>
        <p className="c2__note">
          <b>Built but unused in v1</b> (DECISIONS.md: no hearts / gems). Shipped with the system so enabling either
          later is a prop flip, not a rebuild.
        </p>
        <div className="c2__row c2__row--centered">
          <HeartsBar current={hearts} total={5} size="md" />
          <GemsCounter gems={250} />
        </div>
        <div className="c2__controls">
          <Button variant="primary" size="sm" onClick={() => setHearts((h) => Math.min(5, h + 1))}>
            +1 გული
          </Button>
          <Button variant="danger" size="sm" onClick={() => setHearts((h) => Math.max(0, h - 1))}>
            -1 გული
          </Button>
        </div>
      </section>

      <footer className="c2__footer">
        <p>
          <b>Cluster 2 review.</b> Tell Jake: XP bump feel, streak flame weight, bar shine, ring thickness.
        </p>
      </footer>
    </main>
  );
}
